import { useCallback, useEffect, useState } from "react";
import useFetchAndTrace, {
  FetchAndTraceErrorHandler,
  FetchAndTraceHandler,
} from "./fetchWithTrace";
import { ShippingRate } from "../../reducers/shoppingCartOrder";
import settings from "../../constants/settings";
import { useCustomAlert } from "./alerts";
import { CURRENCY, ShippingAddress } from "../../reducers/uploadedBoard";

export interface ShippingRatesResponse {
  rates: ShippingRate[];
  shipping_rates_restricted_to_ground: boolean;
}
export default function useGetShippingRates(
  shipmentId: string,
  userId: string,
  shippingAddress: ShippingAddress,
  shipmentTotal: number,
  currency: CURRENCY,
  seeAllServiceLevels?: boolean,
) {
  const [shippingRates, setShippingRates] = useState<ShippingRate[] | null>(
    null,
  );
  const [shippingRateRestrictedToGround, setShippingRateRestrictedToGround] =
    useState<boolean>(false);
  const fetchAndTrace = useFetchAndTrace();
  const customAlert = useCustomAlert();

  const getAddressRatesHandler: FetchAndTraceHandler = useCallback(
    async (response) => {
      if (response.ok) {
        const ShippingRatesResponse =
          (await response.json()) as ShippingRatesResponse;
        setShippingRates(ShippingRatesResponse.rates);
        setShippingRateRestrictedToGround(
          ShippingRatesResponse.shipping_rates_restricted_to_ground,
        );
      } else if (response.status == 400) {
        const { detail } = (await response.json()) as {
          detail: string[];
        };
        customAlert(detail.join(", "));
      } else {
        throw Error("Failed to get shipping rates");
      }
    },
    [customAlert],
  );

  const errorHandler: FetchAndTraceErrorHandler = useCallback(() => {
    customAlert("Failed to get shipping rates");
  }, [customAlert]);

  const getShippingOptions = useCallback(() => {
    void fetchAndTrace(
      `${settings.api_url}/users/${userId}/shopping-cart/shipping-address-rates`,
      getAddressRatesHandler,
      errorHandler,
      {
        body: JSON.stringify({
          shipment_id: shipmentId,
          shipping_address: shippingAddress,
          boards_total: shipmentTotal,
          currency: currency,
          see_all_service_levels: seeAllServiceLevels,
        }),
        method: "PUT",
      },
    );
  }, [
    currency,
    errorHandler,
    fetchAndTrace,
    getAddressRatesHandler,
    shipmentTotal,
    shippingAddress,
    userId,
    seeAllServiceLevels,
    shipmentId,
  ]);

  useEffect(() => {
    if (shippingRates === null) {
      getShippingOptions();
    }
  }, [getShippingOptions, shippingRates]);

  return {
    shippingRates,
    shippingRateRestrictedToGround,
  };
}
